import { ArrowLeft, Info, MoreVertical } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";
import BottomNavBar from "../../components/BottomNavBar";

const StressIndexInputsPage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-20"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-base font-semibold text-foreground">
            Stress Index Inputs
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <MoreVertical className="h-5 w-5" />
          </Button>
        </div>

        {/* Stress Level Cards */}
        <div className="flex gap-2">
          <Card className="flex-1 rounded-xl bg-gradient-to-br from-orange-50/90 to-orange-100/50 p-3 shadow-sm dark:from-orange-950/20 dark:to-orange-900/10">
            <span className="mb-2 inline-block rounded-md bg-orange-200/80 px-2 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/50 dark:text-orange-400">
              Confidence
            </span>
            <div className="mb-1 text-3xl font-bold text-foreground">--</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              No data available
            </div>
          </Card>

          <Card className="flex-1 rounded-xl bg-gradient-to-br from-orange-50/90 to-orange-100/50 p-3 shadow-sm dark:from-orange-950/20 dark:to-orange-900/10">
            <span className="mb-2 inline-block rounded-md bg-orange-200/80 px-2 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/50 dark:text-orange-400">
              Correlation Window
            </span>
            <div className="mb-1 text-lg font-bold text-foreground">--</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              No data available
            </div>
          </Card>
        </div>

        {/* HRV Circulation */}
        <Card className="rounded-xl bg-white/60 p-3 shadow-sm dark:bg-gray-800/60">
          <div className="mb-2 flex items-start justify-between">
            <div>
              <div className="text-base font-bold text-foreground">
                HRV Circulation
              </div>
              <div className="text-sm font-semibold text-foreground">
                -- vs baseline
              </div>
            </div>
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              No Data
            </span>
          </div>
          <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
            Lower HRV relative to your baseline increases physiological load.
          </p>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-green-500"
              style={{ width: "0%" }}
            />
          </div>
        </Card>

        {/* Sleep Recovery */}
        <Card className="rounded-xl bg-white/60 p-3 shadow-sm dark:bg-gray-800/60">
          <div className="mb-2 flex items-start justify-between">
            <div>
              <div className="text-base font-bold text-foreground">
                Sleep Recovery
              </div>
              <div className="text-sm font-semibold text-foreground">-- hrs</div>
            </div>
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              No Data
            </span>
          </div>
          <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
            Sleep debt reduces recovery and increases stress load.
          </p>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-green-500"
              style={{ width: "0%" }}
            />
          </div>
        </Card>

        {/* Resting Heart Rate Delta */}
        <Card className="rounded-xl bg-white/60 p-3 shadow-sm dark:bg-gray-800/60">
          <div className="mb-2 flex items-start justify-between">
            <div>
              <div className="text-base font-bold text-foreground">
                Resting Heart Rate Delta
              </div>
              <div className="text-sm font-semibold text-foreground">-- bpm</div>
            </div>
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              No Data
            </span>
          </div>
          <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
            Higher resting HR can indicate sympathetic activation.
          </p>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-green-500"
              style={{ width: "0%" }}
            />
          </div>
        </Card>

        {/* Activity Recovery Buffer */}
        <Card className="rounded-xl bg-white/60 p-3 shadow-sm dark:bg-gray-800/60">
          <div className="mb-2 flex items-start justify-between">
            <div>
              <div className="text-base font-bold text-foreground">
                Activity Recovery Buffer
              </div>
              <div className="text-sm font-semibold text-foreground">
                -- active recovery
              </div>
            </div>
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              No Data
            </span>
          </div>
          <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
            Light movement can modulate better physiological load.
          </p>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-green-500"
              style={{ width: "0%" }}
            />
          </div>
        </Card>

        {/* Info Box */}
        <Card className="rounded-xl bg-blue-50/80 p-3 shadow-sm dark:bg-blue-900/20">
          <div className="flex gap-2">
            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40">
              <Info className="h-3 w-3 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
              <span className="font-semibold text-foreground">
                Your Stress Index is calculated from HRV deviation, sleep
                quality trends, relative effort, stress adjustment is applied.
              </span>{" "}
              Continuous trends have been analyzed. Improving trends to be more metabolically
              sound could help upregulate recovery signals and overall heart rate variability.
            </div>
          </div>
        </Card>
      </div>

      {/* Bottom Navigation Bar */}
      <BottomNavBar activeIndex={3} />
    </div>
  );
};

export default StressIndexInputsPage;
